// src/components/macro/MacroTradingViewChart.tsx

import React, { useEffect, useRef, memo } from "react";
import {
  TRADINGVIEW_CSS_CLASSES,
  TRADINGVIEW_SCRIPTS,
  WIDGET_LOAD_TIMEOUT,
  TRADINGVIEW_ERROR_MESSAGES,
} from "@/constants/technicalAnalysis/tradingView";
import { LineChart, Banknote, Gem } from "lucide-react";

// Static icon map for performance (avoid DynamicIcon)
const ICON_MAP = {
  "line-chart": LineChart,
  "banknote": Banknote,
  "gem": Gem,
} as const;

type MacroIconName = keyof typeof ICON_MAP;

interface MacroTradingViewChartProps {
  symbol: string;
  title?: string;
  subtitle?: string;
  iconName?: MacroIconName;
  interval?: string; // "D", "W", "M", "3M" etc.
  range?: string; // "1M", "12M", "60M", "ALL" etc.
  height?: number;
  className?: string;
  actions?: React.ReactNode;
}

/**
 * MacroTradingViewChart
 *
 * A specialized TradingView chart for Macro indicators (DXY, M2, Debt, etc.).
 * Styled to match TradingViewPriceChartCard for consistency.
 */
export const MacroTradingViewChart: React.FC<MacroTradingViewChartProps> = ({
  symbol,
  title = "Chart",
  subtitle,
  iconName = "line-chart",
  interval = "D",
  range,
  height = 300,
  className = "",
  actions,
}) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const scriptLoadedRef = useRef<boolean>(false);
  const timeoutRef = useRef<NodeJS.Timeout>();

  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    // Clear existing content
    container.innerHTML = "";
    scriptLoadedRef.current = false;

    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }

    // Create widget container
    const widgetContainer = document.createElement("div");
    widgetContainer.className = TRADINGVIEW_CSS_CLASSES.WIDGET;
    widgetContainer.style.height = "100%";
    widgetContainer.style.width = "100%";
    container.appendChild(widgetContainer);

    // Create script
    const script = document.createElement("script");
    script.src = TRADINGVIEW_SCRIPTS.ADVANCED_CHART;
    script.type = "text/javascript";
    script.async = true;

    // Match card background exactly: #161F2D
    const cardBackground = "#161F2D";
    const toolbarBackground = "#161F2D";

    // Configuration - matching TradingViewPriceChartCard
    const widgetConfig = {
      symbol,
      interval,
      timezone: "Etc/UTC",

      // Theme & appearance
      theme: "dark",
      style: "1", // Candlesticks
      locale: "en",

      // Colors - aligned with app card background
      backgroundColor: cardBackground,
      gridColor: "rgba(255, 255, 255, 0.04)",

      // Toolbar & UI
      toolbar_bg: toolbarBackground,
      hide_top_toolbar: false,
      hide_side_toolbar: true,
      hide_legend: true,
      hide_volume: false,

      // Symbol controls
      allow_symbol_change: true,

      // Features
      save_image: false,
      calendar: false,
      details: false,
      hotlist: false,
      withdateranges: true,

      // Layout
      autosize: true,
      enable_publishing: false,

      // Initial Zoom Range
      ...(range && { range }),

      // Studies/indicators
      studies: [],

      // Overrides for customization
      overrides: {
        // Candle colors
        "mainSeriesProperties.candleStyle.upColor": "#34d399",
        "mainSeriesProperties.candleStyle.downColor": "#f87171",
        "mainSeriesProperties.candleStyle.borderUpColor": "#34d399",
        "mainSeriesProperties.candleStyle.borderDownColor": "#f87171",
        "mainSeriesProperties.candleStyle.wickUpColor": "#34d399",
        "mainSeriesProperties.candleStyle.wickDownColor": "#f87171",

        // Background
        "paneProperties.background": cardBackground,
        "paneProperties.backgroundType": "solid",
        "paneProperties.backgroundGradientStartColor": cardBackground,
        "paneProperties.backgroundGradientEndColor": cardBackground,

        // Grid
        "paneProperties.vertGridProperties.color": "rgba(255, 255, 255, 0.04)",
        "paneProperties.horzGridProperties.color": "rgba(255, 255, 255, 0.04)",
        "paneProperties.vertGridProperties.style": 0,
        "paneProperties.horzGridProperties.style": 0,

        // Scales
        "scalesProperties.textColor": "rgba(255, 255, 255, 0.6)",
        "scalesProperties.lineColor": "rgba(255, 255, 255, 0.08)",

        // Volume
        volumePaneSize: "small",
        "mainSeriesProperties.volumeStyle.volume.color.0":
          "rgba(248, 113, 113, 0.5)",
        "mainSeriesProperties.volumeStyle.volume.color.1":
          "rgba(52, 211, 153, 0.5)",

        // Legend
        "paneProperties.legendProperties.showLegend": true,
        "paneProperties.legendProperties.showSeriesTitle": true,
        "paneProperties.legendProperties.showSeriesOHLC": true,
        "paneProperties.legendProperties.showBarChange": true,
        "paneProperties.legendProperties.backgroundTransparency": 100,
        "paneProperties.legendProperties.backgroundColor": "rgba(0, 0, 0, 0)",
      },

      // Disabled features
      disabled_features: [
        "header_symbol_search",
        "symbol_search_hot_key",
        "header_compare",
        "compare_symbol",
        "header_screenshot",
        "header_saveload",
        "header_undo_redo",
        "go_to_date",
        "context_menus",
        "border_around_the_chart",
        "remove_library_container_border",
      ],

      // Enabled features
      enabled_features: [
        "hide_left_toolbar_by_default",
        "side_toolbar_in_fullscreen_mode",
        "header_in_fullscreen_mode",
      ],
    };

    script.innerHTML = JSON.stringify(widgetConfig);

    script.onerror = () => {
      console.error(TRADINGVIEW_ERROR_MESSAGES.SCRIPT_LOAD_FAILED);
      if (container) {
        container.innerHTML = `
          <div class="flex items-center justify-center h-full bg-gray-800/20 rounded text-red-400">
            <div class="text-center">
              <p class="text-sm">${TRADINGVIEW_ERROR_MESSAGES.SCRIPT_LOAD_FAILED}</p>
              <p class="text-xs text-gray-500 mt-1">Please refresh the page to try again</p>
            </div>
          </div>
        `;
      }
    };

    script.onload = () => {
      scriptLoadedRef.current = true;
      if (timeoutRef.current) clearTimeout(timeoutRef.current);
    };

    // Timeout fallback
    timeoutRef.current = setTimeout(() => {
      if (!scriptLoadedRef.current && container) {
        container.innerHTML = `
          <div class="flex items-center justify-center h-full bg-gray-800/20 rounded text-yellow-400">
            <div class="text-center">
              <p class="text-sm">Chart loading...</p>
              <p class="text-xs text-gray-500 mt-1">This may take a moment</p>
            </div>
          </div>
        `;
      }
    }, WIDGET_LOAD_TIMEOUT);

    widgetContainer.appendChild(script);

    return () => {
      if (timeoutRef.current) clearTimeout(timeoutRef.current);
      if (container) container.innerHTML = "";
    };
  }, [symbol, interval, range]);

  return (
    <div className={`rounded-xl bg-gray-800/40 p-4 ${className}`}>
      {/* Header matching TradingViewPriceChartCard */}
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <div className="w-6 h-6 rounded-md bg-blue-500/15 text-blue-400 flex items-center justify-center">
            {(() => {
              const IconComponent = ICON_MAP[iconName];
              return <IconComponent className="w-3.5 h-3.5" />;
            })()}
          </div>
          <div>
            <div className="text-xs uppercase tracking-wide text-slate-300">
              {title}
            </div>
            {subtitle && (
              <div className="text-[12px] text-slate-400">{subtitle}</div>
            )}
          </div>
        </div>
        {actions && <div className="flex items-center gap-3">{actions}</div>}
      </div>

      {/* Chart Container */}
      <div
        ref={containerRef}
        className={`${TRADINGVIEW_CSS_CLASSES.CONTAINER} overflow-hidden rounded-lg tradingview-custom`}
        style={{
          height: `${height}px`,
          width: "100%",
          backgroundColor: "#161F2D",
        }}
      >
        <div className="flex items-center justify-center h-full rounded animate-pulse">
          <div className="text-center">
            <div className="w-8 h-8 border-2 border-blue-500 border-t-transparent rounded-full animate-spin mx-auto mb-2"></div>
            <p className="text-sm text-gray-400">Loading chart...</p>
          </div>
        </div>
      </div>

      {/* Custom CSS to override TradingView styles */}
      <style>{`
        .tradingview-custom,
        .tradingview-custom > div,
        .tradingview-custom iframe {
          border: none !important;
          background: #161F2D !important;
        }
        .tradingview-custom * {
          --tv-color-pane-background: #161F2D !important;
        }
        .tradingview-custom [class*="legend"] {
          background: transparent !important;
        }
        .tradingview-custom [class*="title"] {
          color: rgba(255, 255, 255, 0.9) !important;
        }
      `}</style>
    </div>
  );
};

export default memo(MacroTradingViewChart);
